import {Avatar, Choice, Separator, showNotification} from 'platform/components';
import {Box, Heading, HStack, Space, Text, VStack} from 'platform/foundation';

import {useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {head, isNil} from 'ramda';

import {useLogoutMutation} from '@dms/api/core';
import {
  selectCurrentUserInfo,
  useGetUserAvatarUrlQuery,
  useSetUserAwayMutation,
  useSetUserSettingsItemMutation,
} from '@dms/api/customer';
import {selectActiveBranchId, selectUserBranchOptions, setActiveBranchId} from '@dms/api/features';
import {SetUserSettingsItemApiArg} from '@dms/api/shared';
import {browserStorageKey} from '@dms/config';
import i18n from '@dms/i18n';
import {
  getWorkspaceFromUri,
  myProfileRoutes,
  redirectToWorkspace,
  settingsRoutes,
} from '@dms/routes';
import {EMPTY_PLACEHOLDER, handleApiError, usePermissions, useUserFullName} from '@dms/shared';

import {GtmEvent, pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {omneticReleaseNotes} from '../../../releaseNotes';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {LatestReleaseLink} from './LatestReleaseLink';
import {ShowOnMobileNavigation} from './ShowOnMobileNavigation';
import {TopBarItem, TopBarItemRef} from './TopBarItem';
import {TopBarMenuItem} from './TopBarMenuItem';
import {UserAvatarButton} from './UserAvatarButton';

const DEFAULT_APP_VERSION = '1.9.0';
const LATEST_APP_VERSION = head(omneticReleaseNotes)?.versionNumber ?? DEFAULT_APP_VERSION;

export function UserAvatar(props: RequiredTestIdProps) {
  const dispatch = useDispatch();
  const topBarItemRef = useRef<TopBarItemRef>(null);
  const isMobileNavigation = useIsMobileNavigation();

  const [hasReadSettingsPermission] = usePermissions({permissionKeys: ['readSettings']});
  const [logoutUser] = useLogoutMutation();
  const {data: userInfo} = useSelector(selectCurrentUserInfo);
  const {data: userAvatarUrl} = useGetUserAvatarUrlQuery(
    {userId: userInfo?.id ?? '', height: 120, width: 120},
    {skip: isNil(userInfo)}
  );

  const userFullName = useUserFullName();
  const activeBranchId = useSelector(selectActiveBranchId);
  const branchOptions = useSelector(selectUserBranchOptions);
  const [updateUserSettings] = useSetUserSettingsItemMutation();
  const [setUserAway, {isLoading: isAwayLoading}] = useSetUserAwayMutation();

  const handleClosePopup = () => {
    if (isMobileNavigation) {
      topBarItemRef.current?.close();
    }
  };

  const handleBranchChange = (value: string | number | string[] | null) => {
    if (typeof value !== 'string') {
      return;
    }

    const arg: SetUserSettingsItemApiArg = {
      settingKey: 'active_branch',
      userSettingRequestBody: {value},
    };

    const gtmEvent: GtmEvent = {
      event: 'button_click',
      event_id: 'top_bar_branch_select',
      previous_value: {
        branchId: activeBranchId,
      },
      new_value: {
        branchId: value,
      },
    };

    updateUserSettings(arg)
      .unwrap()
      .then(() => {
        handleClosePopup();
      })
      .then(() => {
        dispatch(setActiveBranchId(value));
        pushGtmEvent(gtmEvent);
      })
      .catch(handleApiError);
  };

  const handleToggleAway = () => {
    if (isNil(userInfo)) {
      return;
    }

    const newIsAway = !(userInfo.isAway ?? false);

    setUserAway({userId: userInfo.id, isAway: newIsAway})
      .unwrap()
      .then(() => {
        showNotification.success(
          newIsAway ? i18n.t('user.awayStatus.toastAwayOn') : i18n.t('user.awayStatus.toastAwayOff')
        );
      })
      .catch(handleApiError);
  };

  const handleLogout = () => {
    const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
    const {workspace} = getWorkspaceFromUri();

    logoutUser({accessToken, workspace})
      .unwrap()
      .then(() => {
        pushGtmEvent({
          event: 'button_click',
          event_id: 'user_logout',
        });
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem(browserStorageKey.LAST_PROMO_FEATURE_SEEN, i18n.t('ad.feature.id'));
        localStorage.setItem(browserStorageKey.LAST_KNOWN_LANGUAGE, i18n.language);
        redirectToWorkspace({currentUserEmail: userInfo?.email});
      })
      .catch(handleApiError);
  };

  const getBranchByActiveId = useMemo(
    () => branchOptions?.find((b) => b.value === activeBranchId) || null,
    [branchOptions, activeBranchId]
  );

  if (!userInfo) {
    return null;
  }

  return (
    <TopBarItem
      gtmEventId="top_bar_user_avatar_open"
      ref={topBarItemRef}
      isFullScreen={isMobileNavigation}
      trigger={(isOpen) => (
        <UserAvatarButton
          isOpen={isOpen}
          isAway={userInfo.isAway ?? false}
          userAvatarUrl={userAvatarUrl}
          data-testid={suffixTestId('myProfileAndSettings-button', props)}
        />
      )}
    >
      <Box backgroundColor="general.white" padding={4}>
        <Box width={['auto', 'auto', 75]}>
          <HStack align="center">
            <Avatar
              data-testid={suffixTestId('myProfileAndSettings', props)}
              name={userFullName}
              size="default"
              imageUrl={userAvatarUrl}
              variant="circle"
            />

            <Space horizontal={4} />
            <VStack width="100%">
              <Heading
                alternative
                headingLevel={2}
                size={4}
                data-testid={suffixTestId('myProfileAndSettings-name', props)}
              >
                {userFullName ?? EMPTY_PLACEHOLDER}
              </Heading>
              <Text
                size="small"
                color="secondary"
                overflowWrap="anywhere"
                data-testid={suffixTestId('myProfileAndSettings-email', props)}
              >
                {userInfo.email}
              </Text>
              <Text
                size="xSmall"
                color="secondary"
                overflowWrap="anywhere"
                data-testid={suffixTestId('myProfileAndSettings-version', props)}
              >
                {i18n.t('general.labels.appVersion') + `: ${LATEST_APP_VERSION}`}
              </Text>
            </VStack>
          </HStack>

          <ShowOnMobileNavigation>
            <Separator spacing={4} />
            {branchOptions && branchOptions.length > 1 ? (
              <Choice
                label={i18n.t('entity.branch.labels.selectedBranch')}
                data-testid={suffixTestId('myProfileAndSettings-activeBranch', props)}
                name="activeBranch"
                value={activeBranchId || null}
                options={branchOptions}
                onChange={handleBranchChange}
                closeMenuOnScroll={false}
                isNotClearable
                maxMenuHeight={33}
              />
            ) : (
              <Heading
                data-testid={suffixTestId('myProfileAndSettings-activeBranch', props)}
                headingLevel={2}
                size={4}
              >
                {getBranchByActiveId?.label ?? EMPTY_PLACEHOLDER}
              </Heading>
            )}
            <Space vertical={4} />
            <TopBarMenuItem
              as="button"
              onClick={handleToggleAway}
              icon={userInfo.isAway ? 'action/check_circle' : 'alert/warning'}
              label={
                userInfo.isAway
                  ? i18n.t('user.awayStatus.goAvailable')
                  : i18n.t('user.awayStatus.goAway')
              }
              isLoading={isAwayLoading}
              isDisabled={isAwayLoading}
              data-testid="layout-header-away-toggle"
            />
          </ShowOnMobileNavigation>
        </Box>

        <Separator spacing={4} />
        <VStack spacing={1}>
          <TopBarMenuItem
            to={myProfileRoutes.personalInformation}
            onClick={handleClosePopup}
            icon="social/person"
            label={i18n.t('entity.user.labels.myProfile')}
          />
          <ShowOnMobileNavigation when={hasReadSettingsPermission}>
            <TopBarMenuItem
              to={settingsRoutes.userManagement}
              onClick={handleClosePopup}
              icon="action/settings"
              label={i18n.t('page.settings.labels.title')}
            />
          </ShowOnMobileNavigation>
          <ShowOnMobileNavigation>
            <LatestReleaseLink />
          </ShowOnMobileNavigation>
        </VStack>
        <Separator spacing={4} />
        <TopBarMenuItem
          as="button"
          onClick={handleLogout}
          label={i18n.t('general.actions.logout')}
          data-testid={suffixTestId('myProfileAndSettings-logout', props)}
          icon="action/exit_to_app"
        />
      </Box>
    </TopBarItem>
  );
}
